/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

const messages = defineMessages({
    move: { id: 'clu.cluMoveUtterancesModal.move', defaultMessage: 'Move' },
    placeholder: { id: 'clu.cluMoveUtterancesModal.placeholder', defaultMessage: 'Select intent' },
    modalTitle: { id: 'clu.cluMoveUtterancesModal.modalTitle', defaultMessage: 'Move selected utterances' },
    description: {
        id: 'clu.cluMoveUtterancesModal.description',
        defaultMessage: `The selected utterances will move to the intent chosen below.
        Utterances cannot be added to connected intents.`
    },
    intentsDropDownAriaLabel: {
        id: 'clu.cluMoveUtterancesModal.intentsDropDownAriaLabel',
        defaultMessage: 'Intents'
    }
});

export const CluMoveUtterancesModal = withModalMounter(
    observer((props: BasicModalProps<{ selectedIntentId: string }>) => {
        const { localize } = useLocalization();
        const { rootStore } = useGlobalState();

        const [selectedIntentId, setSelectedIntentId] = React.useState('');

        const currentProject = rootStore.currentCluProjectStore;
        const intentsOptions: IDropdownOption[] = currentProject.intentStoreHouse.items.map(intent => ({
            key: intent.id,
            text: intent.name,
            disabled: Boolean(intent.orchestrationType)
        }));

        return (
            <BasicModal
                {...props}
                valid={Boolean(selectedIntentId)}
                doneButtonText={localize(messages.move)}
                onSubmit={() => props.onSubmit({ selectedIntentId })}
                dialogContentProps={{
                    title: localize(messages.modalTitle),
                    subText: localize(messages.description),
                    styles: { inner: { overflowY: 'unset' } }
                }}>
                <LuisDropdown
                    options={intentsOptions}
                    selectedKey={selectedIntentId}
                    placeholder={localize(messages.placeholder)}
                    ariaLabel={localize(messages.intentsDropDownAriaLabel)}
                    onChange={(_, option) => setSelectedIntentId(option.key as string)}
                />
            </BasicModal>
        );
    })
);
